// ═══════════════════════════════════════════════════════════════════
//  BackToSearchPill — "← Back to search results for 'foo'" breadcrumb.
// ═══════════════════════════════════════════════════════════════════
//
//  Mount this on any page that wants to honor a from-search visit.
//  It reads history.state.fromSearch (Phase F) and renders nothing
//  when the user arrived directly.
//
// ═══════════════════════════════════════════════════════════════════

import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useFromSearch } from '@/hooks/search/useFromSearch';
import { useSearch } from '@/contexts/SearchContext';

export interface BackToSearchPillProps {
  className?: string;
}

export function BackToSearchPill({ className }: BackToSearchPillProps) {
  const fromSearch = useFromSearch();
  const navigate = useNavigate();
  const { query, scope } = useSearch();
  if (!fromSearch) return null;

  const q = fromSearch.query ?? query;
  const sc = fromSearch.scope ?? scope;

  const onClick = () => {
    if (q) {
      const params = new URLSearchParams({ q, scope: sc });
      navigate(`/search?${params.toString()}`);
    } else {
      navigate(-1);
    }
  };

  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1 rounded-full border bg-background px-2.5 py-1 text-xs text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground ${className ?? ''}`}
    >
      <ArrowLeft size={12} />
      Back to search{q ? ` for "${q}"` : ''}
    </button>
  );
}
